import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Phone, Mail, Instagram, Users, Clock, Star, ChevronRight, Send, MapPin, FileText, AlertCircle, X, Shield, ScrollText } from 'lucide-react';

const VbeeBeautyLanding = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCGU, setShowCGU] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const services = [
    {
      id: 'microblading',
      title: 'Microblading',
      price: '260€',
      description: 'Technique manuelle qui permet de recréer l\'illusion de poils fins et naturels, un à un, pour un résultat ultra-réaliste.',
      benefits: [
        'Sourcils naturels dessinés poil par poil',
        'Redéfinir des sourcils clairsemés',
        'Gagner du temps le matin',
        'Rendu doux et harmonieux'
      ],
      image: 'https://customer-assets.emergentagent.com/job_04bc52f2-8709-4c23-8aa3-6e57db3fbcd6/artifacts/ni91tbf7_IMG_1863.JPG',
      badge: 'Populaire'
    },
    {
      id: 'microshading',
      title: 'Microshading',
      price: '260€',
      description: 'Technique de maquillage semi-permanent qui crée un effet ombré et poudrée pour des sourcils parfaitement définis.',
      benefits: [
        'Effet ombré et naturel',
        'Parfait pour peaux grasses',
        'Longue durée',
        'Résultat sophistiqué'
      ],
      image: 'https://customer-assets.emergentagent.com/job_04bc52f2-8709-4c23-8aa3-6e57db3fbcd6/artifacts/76s6hlxy_IMG_1864.JPG',
      badge: 'Technique avancée'
    },
    {
      id: 'mixbrows',
      title: 'Mix Brows',
      price: '280€',
      description: 'Combinaison parfaite du microblading et du microshading pour un résultat hybride naturel et structuré.',
      benefits: [
        'Meilleur des deux techniques',
        'Résultat très naturel',
        'Adapté à tous types de peau',
        'Effet tridimensionnel'
      ],
      image: 'https://customer-assets.emergentagent.com/job_04bc52f2-8709-4c23-8aa3-6e57db3fbcd6/artifacts/ikurrn0j_IMG_1867.JPG',
      badge: 'Recommandé'
    },
    {
      id: 'candylips',
      title: 'Candy Lips',
      price: '180€',
      description: 'Offrez à vos lèvres un effet gourmand, doux et naturel avec la technique Candy Lips. Cette prestation de maquillage semi-permanent colore délicatement vos lèvres pour un rendu frais, lumineux et subtilement glossy.',
      benefits: [
        'Effet "bouche sucrée" et pulpeuse',
        'Résultat frais et lumineux',
        'Rendu subtilement glossy',
        'Sans maquillage supplémentaire au quotidien'
      ],
      image: 'https://customer-assets.emergentagent.com/job_glowing-brows/artifacts/knz1uu8k_IMG_5622.JPG',
      badge: 'Nouveau'
    }
  ];

  const galleryImages = [
    {
      url: 'https://customer-assets.emergentagent.com/job_04bc52f2-8709-4c23-8aa3-6e57db3fbcd6/artifacts/ni91tbf7_IMG_1863.JPG',
      title: 'Microblading - Résultat naturel'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_04bc52f2-8709-4c23-8aa3-6e57db3fbcd6/artifacts/76s6hlxy_IMG_1864.JPG',
      title: 'Microshading - Effet ombré'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_04bc52f2-8709-4c23-8aa3-6e57db3fbcd6/artifacts/ikurrn0j_IMG_1867.JPG',
      title: 'Mix Brows - Technique hybride'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_glowing-brows/artifacts/knz1uu8k_IMG_5622.JPG',
      title: 'Candy Lips - Maquillage lèvres'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        alert('Votre message a été envoyé avec succès ! Nous vous recontacterons rapidement.');
        setFormData({ nom: '', email: '', telephone: '', service: '', message: '' });
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      alert('Une erreur est survenue lors de l\'envoi. Veuillez réessayer ou nous contacter directement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // CGU Modal Component
  const CGUModal = () => (
    <Dialog open={showCGU} onOpenChange={setShowCGU}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-black text-white border-rose-500/20">
        <DialogHeader className="border-b border-rose-500/20 pb-4">
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-rose-400 to-amber-300 bg-clip-text text-transparent flex items-center">
            <ScrollText className="h-6 w-6 text-rose-400 mr-3" />
            Conditions Générales d'Utilisation
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-6 space-y-6 text-gray-300 leading-relaxed">
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">1. Objet</h3>
            <p>Les présentes conditions générales d'utilisation (CGU) régissent l'utilisation du site web de VBee Beauty, salon privé spécialisé dans le maquillage semi-permanent.</p>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">2. Prestations</h3>
            <p>VBee Beauty propose des services de maquillage semi-permanent incluant :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Microblading - 260€</li>
              <li>Microshading - 260€</li>
              <li>Mix Brows - 280€</li>
              <li>Candy Lips - 180€</li>
            </ul>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">3. Prise de rendez-vous</h3>
            <p>La prise de rendez-vous s'effectue via le formulaire de contact du site ou par téléphone au 06 52 28 59 40. Toute demande de rendez-vous est soumise à validation et confirmation de notre part.</p>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">4. Contre-indications</h3>
            <p>Certaines contre-indications peuvent s'appliquer aux prestations de maquillage semi-permanent :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Grossesse et allaitement</li>
              <li>Prise d'anticoagulants</li>
              <li>Diabète non contrôlé</li>
              <li>Maladies auto-immunes</li>
              <li>Problèmes de cicatrisation</li>
            </ul>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">5. Annulation et report</h3>
            <p>Toute annulation ou report de rendez-vous doit être signalé au minimum 48h à l'avance. En cas d'annulation tardive, des frais peuvent s'appliquer.</p>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">6. Responsabilité</h3>
            <p>VBee Beauty met tout en œuvre pour assurer des prestations de qualité. Le client s'engage à suivre les recommandations post-soin pour optimiser les résultats.</p>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">7. Propriété intellectuelle</h3>
            <p>Le contenu du site web (textes, images, logo) est protégé par les droits d'auteur. Toute reproduction sans autorisation est interdite.</p>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">8. Contact</h3>
            <p>Pour toute question concernant ces CGU :</p>
            <div className="bg-gray-900 p-4 rounded-lg mt-2">
              <p><strong>VBee Beauty</strong></p>
              <p>Email : vbeebeauty@gmail.com</p>
              <p>Téléphone : 06 52 28 59 40</p>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Privacy Policy Modal Component
  const PrivacyModal = () => (
    <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-black text-white border-rose-500/20">
        <DialogHeader className="border-b border-rose-500/20 pb-4">
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-rose-400 to-amber-300 bg-clip-text text-transparent flex items-center">
            <Shield className="h-6 w-6 text-rose-400 mr-3" />
            Politique de Confidentialité
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-6 space-y-6 text-gray-300 leading-relaxed">
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">1. Collecte des données</h3>
            <p>VBee Beauty collecte les données personnelles suivantes via le formulaire de contact :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Nom et prénom</li>
              <li>Adresse email</li>
              <li>Numéro de téléphone</li>
              <li>Service souhaité</li>
              <li>Message (optionnel)</li>
            </ul>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">2. Finalité du traitement</h3>
            <p>Les données collectées sont utilisées exclusivement pour :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Répondre à vos demandes de renseignements</li>
              <li>Organiser et confirmer vos rendez-vous</li>
              <li>Vous contacter concernant nos prestations</li>
              <li>Améliorer la qualité de nos services</li>
            </ul>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">3. Base légale</h3>
            <p>Le traitement de vos données personnelles repose sur votre consentement libre et éclairé, donné lors de la soumission du formulaire de contact.</p>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">4. Conservation des données</h3>
            <p>Vos données personnelles sont conservées pendant une durée de 3 ans maximum à compter de votre dernier contact avec VBee Beauty.</p>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">5. Partage des données</h3>
            <p>VBee Beauty s'engage à ne jamais vendre, louer ou céder vos données personnelles à des tiers. Vos informations restent strictement confidentielles.</p>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">6. Vos droits</h3>
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Droit d'accès à vos données</li>
              <li>Droit de rectification</li>
              <li>Droit d'effacement</li>
              <li>Droit à la portabilité</li>
              <li>Droit d'opposition</li>
              <li>Droit de limitation du traitement</li>
            </ul>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">7. Sécurité</h3>
            <p>VBee Beauty met en place des mesures techniques et organisationnelles appropriées pour protéger vos données contre l'accès non autorisé, la perte ou la destruction.</p>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">8. Cookies</h3>
            <p>Notre site utilise des cookies techniques nécessaires au bon fonctionnement. Aucun cookie de tracking ou publicitaire n'est utilisé.</p>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">9. Contact</h3>
            <p>Pour exercer vos droits ou pour toute question relative à cette politique :</p>
            <div className="bg-gray-900 p-4 rounded-lg mt-2">
              <p><strong>VBee Beauty</strong></p>
              <p>Email : vbeebeauty@gmail.com</p>
              <p>Téléphone : 06 52 28 59 40</p>
            </div>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold text-rose-400 mb-3">10. Mise à jour</h3>
            <p>Cette politique de confidentialité peut être mise à jour. La version en vigueur est celle publiée sur notre site web.</p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/90 backdrop-blur-md border-b border-gold-500/20">
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_glowing-brows/artifacts/op7029ot_Image%20%282%29.png" 
              alt="VBee Beauty Logo" 
              className="h-12 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#about" className="nav-link hover:text-rose-400 transition-colors">Qui suis-je ?</a>
            <a href="#services" className="nav-link hover:text-rose-400 transition-colors">Services</a>
            <a href="#gallery" className="nav-link hover:text-rose-400 transition-colors">Galerie</a>
            <a href="#info" className="nav-link hover:text-rose-400 transition-colors">À lire avant RDV</a>
            <a href="#contact" className="nav-link hover:text-rose-400 transition-colors">Contact</a>
          </div>
          <Button 
            className="cta-button bg-rose-500 hover:bg-rose-600 text-white px-6 py-2 rounded-full"
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
          >
            Réserver
          </Button>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0">
          <div className="glow-effect absolute top-1/4 left-1/4 w-96 h-96 bg-rose-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="glow-effect absolute bottom-1/4 right-1/4 w-80 h-80 bg-amber-500/15 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_glowing-brows/artifacts/op7029ot_Image%20%282%29.png" 
              alt="VBee Beauty Logo" 
              className="h-24 md:h-32 w-auto mx-auto mb-6"
            />
          </div>
          
          <h1 className="hero-heading font-display text-5xl md:text-7xl lg:text-8xl font-bold mb-6 bg-gradient-to-r from-rose-400 via-pink-300 to-amber-300 bg-clip-text text-transparent">
            VBEE BEAUTY
          </h1>
          
          <p className="body-large text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Salon privé spécialisé dans la mise en beauté du regard et des lèvres par le maquillage semi-permanent
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg"
              className="cta-button bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white px-8 py-4 rounded-full text-lg font-medium transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-rose-500/25"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Prendre rendez-vous
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
            
            <Button 
              variant="outline"
              size="lg"
              className="border-2 border-rose-400 text-rose-400 hover:bg-rose-400 hover:text-black px-8 py-4 rounded-full text-lg font-medium transition-all duration-300"
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
            >
              Découvrir nos services
            </Button>
          </div>
          
          <div className="mt-12 flex justify-center space-x-8 text-gray-400">
            <div className="text-center">
              <Users className="h-8 w-8 mx-auto mb-2 text-rose-400" />
              <p className="text-sm">Salon privé</p>
            </div>
            <div className="text-center">
              <Star className="h-8 w-8 mx-auto mb-2 text-amber-400" />
              <p className="text-sm">6+ ans d'expérience</p>
            </div>
            <div className="text-center">
              <Clock className="h-8 w-8 mx-auto mb-2 text-blue-400" />
              <p className="text-sm">Résultats durables</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="section-heading text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-rose-400 to-amber-300 bg-clip-text text-transparent">
                Qui suis-je ?
              </h2>
              
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p className="body-large text-lg">
                  Depuis plus de <span className="text-rose-400 font-semibold">6 ans</span>, je me consacre avec passion à l'art du maquillage semi-permanent. Mon objectif : sublimer chaque femme en révélant la beauté unique de son regard et de son sourire.
                </p>
                
                <p className="body-large text-lg">
                  Formée en <span className="text-amber-300 font-semibold">France, aux États-Unis et au Brésil</span>, j'ai acquis une expertise internationale qui me permet d'offrir des résultats à la fois précis, élégants et naturels.
                </p>
                
                <p className="body-large text-lg">
                  Plus qu'un métier, c'est une vraie <span className="text-rose-400 font-semibold">vocation</span> : redonner confiance, magnifier la féminité et créer des résultats durables qui mettent en valeur ce qu'il y a de plus beau chez chacune.
                </p>
                
                <p className="body-large text-lg text-rose-300">
                  Bienvenue dans mon univers, où chaque détail compte et où votre beauté est ma plus belle source d'inspiration. ✨
                </p>
              </div>
            </div>
            
            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-rose-500/20 to-amber-500/20 rounded-2xl blur-xl"></div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_glowing-brows/artifacts/nk8tq17i_IMG_0120.jpg" 
                  alt="Portrait professionnel VBee Beauty" 
                  className="relative rounded-2xl shadow-2xl w-full object-cover aspect-square"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-black">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="section-heading text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-rose-400 to-amber-300 bg-clip-text text-transparent">
              Nos Services
            </h2>
            <p className="body-large text-xl text-gray-300 max-w-3xl mx-auto">
              Découvrez nos techniques de maquillage semi-permanent pour sublimer votre regard et vos lèvres
            </p>
          </div>
          
          <div className="grid gap-8 md:gap-12">
            {services.map((service, index) => (
              <Card key={service.id} className={`project-card bg-gradient-to-br ${index % 2 === 0 ? 'from-gray-900 to-black' : 'from-black to-gray-900'} border-rose-500/20 hover:border-rose-400/40 transition-all duration-300 overflow-hidden group`}>
                <CardContent className="p-0">
                  <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:grid-flow-col-dense' : ''}`}>
                    <div className={`relative overflow-hidden ${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      {service.badge && (
                        <Badge className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium ${
                          service.badge === 'Nouveau' ? 'bg-green-500 text-white' : 
                          service.badge === 'Populaire' ? 'bg-rose-500 text-white' :
                          service.badge === 'Recommandé' ? 'bg-amber-500 text-white' :
                          'bg-purple-500 text-white'
                        }`}>
                          {service.badge}
                        </Badge>
                      )}
                    </div>
                    
                    <div className={`p-8 md:p-12 flex flex-col justify-center ${index % 2 === 1 ? 'md:col-start-1' : ''}`}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="card-heading text-2xl md:text-3xl font-bold text-white">
                          {service.title}
                        </h3>
                        <span className="text-2xl font-bold text-rose-400">
                          {service.price}
                        </span>
                      </div>
                      
                      <p className="body-medium text-gray-300 mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      
                      <div className="space-y-3 mb-8">
                        {service.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-rose-400 rounded-full flex-shrink-0"></div>
                            <span className="text-gray-300">{benefit}</span>
                          </div>
                        ))}
                      </div>
                      
                      <Button 
                        className="cta-button bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white rounded-full px-6 py-3 font-medium transform hover:scale-105 transition-all duration-300"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, service: service.title }));
                          document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        Réserver {service.title}
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="section-heading text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-rose-400 to-amber-300 bg-clip-text text-transparent">
              Galerie de nos réalisations
            </h2>
            <p className="body-large text-xl text-gray-300">
              Découvrez la qualité de notre travail à travers nos réalisations
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {galleryImages.map((image, index) => (
              <div key={index} className="relative group overflow-hidden rounded-2xl">
                <div className="absolute -inset-4 bg-gradient-to-r from-rose-500/20 to-amber-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <img 
                  src={image.url} 
                  alt={image.title}
                  className="relative w-full h-80 object-cover rounded-2xl group-hover:scale-105 transition-transform duration-500 shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent rounded-2xl"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-white font-semibold text-lg mb-2">{image.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Information Section - Expanded Image */}
      <section id="info" className="py-20 px-6 bg-black">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="section-heading text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-rose-400 to-amber-300 bg-clip-text text-transparent">
              À lire avant rendez-vous
            </h2>
            <p className="body-large text-xl text-gray-300">
              Informations importantes pour préparer votre séance
            </p>
          </div>
          
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-rose-500/20 to-amber-500/20 rounded-2xl blur-xl"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_04bc52f2-8709-4c23-8aa3-6e57db3fbcd6/artifacts/qisz9lhb_IMG_1865.JPG" 
                alt="Informations avant rendez-vous" 
                className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
            
            <div className="lg:col-span-2">
              <Card className="bg-gradient-to-br from-gray-900 to-black border-rose-500/20">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <FileText className="h-8 w-8 text-rose-400 mr-3" />
                    <h3 className="text-2xl font-bold text-white">Préparation de votre rendez-vous</h3>
                  </div>
                  
                  <div className="space-y-6 text-gray-300">
                    <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <AlertCircle className="h-5 w-5 text-rose-400 mr-2" />
                        <span className="font-semibold text-rose-400">Important</span>
                      </div>
                      <p className="text-sm">
                        Merci de prendre quelques instants pour lire ces informations avant votre rendez-vous.
                      </p>
                    </div>
                    
                    <div>
                      <p className="mb-4">
                        Afin de préparer au mieux votre prestation, je vous invite à m'envoyer par message trois photos récentes de vos sourcils, prises selon les angles suivants :
                      </p>
                      
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-rose-400 rounded-full mr-3"></div>
                          <span>Profil gauche</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-rose-400 rounded-full mr-3"></div>
                          <span>De face</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-rose-400 rounded-full mr-3"></div>
                          <span>Profil droit</span>
                        </li>
                      </ul>
                      
                      <p className="text-sm text-gray-400">
                        Ces photos permettront d'évaluer la forme naturelle de vos sourcils et d'adapter au mieux le travail à réaliser.
                      </p>
                    </div>
                    
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                      <p className="text-sm text-amber-200">
                        Je vous remercie pour votre collaboration et votre compréhension.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="section-heading text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-rose-400 to-amber-300 bg-clip-text text-transparent">
              Prendre rendez-vous
            </h2>
            <p className="body-large text-xl text-gray-300">
              Contactez-nous pour réserver votre séance de maquillage semi-permanent
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">Informations de contact</h3>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <Phone className="h-6 w-6 text-rose-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Téléphone</p>
                    <a href="tel:0652285940" className="text-gray-300 hover:text-rose-400 transition-colors">
                      06 52 28 59 40
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <Mail className="h-6 w-6 text-rose-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Email</p>
                    <a href="mailto:vbeebeauty@gmail.com" className="text-gray-300 hover:text-rose-400 transition-colors">
                      vbeebeauty@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <Instagram className="h-6 w-6 text-rose-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Suivez-nous</p>
                    <a 
                      href="https://www.instagram.com/vbee_beauty77/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-rose-400 transition-colors"
                    >
                      @vbee_beauty77
                    </a>
                  </div>
                </div>
              </div>
              
              <Separator className="my-8 bg-gray-700" />
              
              <div className="space-y-4">
                <p className="text-white font-medium mb-4">Informations importantes :</p>
                <p className="text-sm text-gray-300 leading-relaxed">
                  Merci d'envoyer 3 photos de vos sourcils (profil gauche, face, profil droit) 
                  avant votre rendez-vous pour une évaluation personnalisée.
                </p>
                
                <Button 
                  variant="outline"
                  className="w-full border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black rounded-full py-3 mt-6"
                  onClick={() => window.open('https://g.page/r/CfD8hWVLIBc6EBM/review', '_blank')}
                >
                  Laisser un avis Google
                  <Star className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-rose-500/20 to-amber-500/20 rounded-2xl blur-xl"></div>
              <Card className="relative bg-gradient-to-br from-gray-900 to-black border-rose-500/20">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-white font-medium mb-2">Nom complet *</label>
                      <Input 
                        type="text"
                        name="nom"
                        value={formData.nom}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-rose-400 focus:ring-rose-400"
                        placeholder="Votre nom et prénom"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white font-medium mb-2">Email *</label>
                      <Input 
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-rose-400 focus:ring-rose-400"
                        placeholder="votre@email.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white font-medium mb-2">Téléphone *</label>
                      <Input 
                        type="tel"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-rose-400 focus:ring-rose-400"
                        placeholder="06 XX XX XX XX"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white font-medium mb-2">Service souhaité</label>
                      <select 
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full bg-gray-800 border border-gray-600 text-white rounded-md px-3 py-2 focus:border-rose-400 focus:ring-rose-400 focus:outline-none"
                      >
                        <option value="">Sélectionner un service</option>
                        <option value="Microblading">Microblading - 260€</option>
                        <option value="Microshading">Microshading - 260€</option>
                        <option value="Mix Brows">Mix Brows - 280€</option>
                        <option value="Candy Lips">Candy Lips - 180€</option>
                        <option value="Consultation">Consultation</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-white font-medium mb-2">Message</label>
                      <Textarea 
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        className="bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-rose-400 focus:ring-rose-400 resize-none"
                        placeholder="Décrivez vos attentes, vos questions ou partagez des informations importantes..."
                      />
                    </div>
                    
                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full cta-button bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white py-4 rounded-full font-medium transform hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Envoi en cours...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <Send className="mr-2 h-4 w-4" />
                          Envoyer ma demande
                        </div>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-black to-gray-900 border-t border-gray-800 py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="text-center md:text-left">
              <img 
                src="https://customer-assets.emergentagent.com/job_glowing-brows/artifacts/op7029ot_Image%20%282%29.png" 
                alt="VBee Beauty Logo" 
                className="h-16 w-auto mx-auto md:mx-0 mb-4"
              />
              <p className="text-gray-400 text-sm">
                Salon privé de maquillage semi-permanent
              </p>
            </div>
            
            <div className="text-center">
              <div className="flex justify-center space-x-6">
                <a 
                  href="https://www.instagram.com/vbee_beauty77/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-rose-400 transition-colors transform hover:scale-110"
                >
                  <Instagram className="h-6 w-6" />
                </a>
                <a 
                  href="https://www.tiktok.com/@vbeebeauty" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-rose-400 transition-colors transform hover:scale-110"
                >
                  <Users className="h-6 w-6" />
                </a>
                <a 
                  href="tel:0652285940"
                  className="text-gray-400 hover:text-rose-400 transition-colors transform hover:scale-110"
                >
                  <Phone className="h-6 w-6" />
                </a>
              </div>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm mb-2">
                © 2025 VBee Beauty - Tous droits réservés
              </p>
              <p className="text-gray-500 text-xs mb-3">
                Maquillage semi-permanent professionnel
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center md:justify-end text-xs">
                <button 
                  onClick={() => setShowCGU(true)}
                  className="text-gray-500 hover:text-rose-400 transition-colors underline"
                >
                  CGU
                </button>
                <span className="text-gray-600 hidden sm:inline">•</span>
                <button 
                  onClick={() => setShowPrivacy(true)}
                  className="text-gray-500 hover:text-rose-400 transition-colors underline"
                >
                  Politique de confidentialité
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <CGUModal />
      <PrivacyModal />
    </div>
  );
};

export default VbeeBeautyLanding;